/** Date-wise item sales from report invoices (client-side). */

const resolveItemKey = (item) => {
  const id = item?.itemId?._id || item?.itemId;
  if (id) return String(id);
  return `${item?.name || "item"}-${item?.unit || ""}`;
};

const normalizeUnix = (value) => {
  if (value == null || value === "") return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n > 1e12 ? Math.floor(n / 1000) : Math.floor(n);
};

/** Prefer backend f_date (YYYY-MM-DD); else local calendar day from unix. */
export const getInvoiceSaleDateKey = (inv) => {
  const f = String(inv?.f_date || "").trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(f)) return f;
  const unix = normalizeUnix(inv?.date);
  if (!unix) return "unknown";
  const d = new Date(unix * 1000);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export const formatSaleDateLabel = (ymd) => {
  if (!ymd || ymd === "unknown") return "Unknown date";
  const [y, m, d] = ymd.split("-").map(Number);
  if (!y || !m || !d) return ymd;
  return new Date(y, m - 1, d).toLocaleDateString("en-PK", {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

/**
 * Aggregate item qty/amount by calendar sale date.
 * @param {Array} salesInvoices - customer + guest sales (include builti-linked if desired)
 */
export const buildDateItemSalesSummary = (salesInvoices = []) => {
  const byDate = new Map();

  for (const inv of salesInvoices) {
    if (!inv || inv.isDeleted === true) continue;
    const dateKey = getInvoiceSaleDateKey(inv);
    if (!byDate.has(dateKey)) {
      byDate.set(dateKey, {
        date: dateKey,
        invoice_count: 0,
        total_qty: 0,
        total_amount: 0,
        items: new Map(),
      });
    }
    const day = byDate.get(dateKey);
    day.invoice_count += 1;

    for (const item of inv.items || []) {
      const itemKey = resolveItemKey(item);
      if (!day.items.has(itemKey)) {
        day.items.set(itemKey, {
          itemId: item?.itemId?._id || item?.itemId || itemKey,
          name: item.name || "-",
          unit: item.unit || "-",
          qty: 0,
          amount: 0,
          invoice_count: 0,
        });
      }
      const row = day.items.get(itemKey);
      const qty = Number(item.qty || 0);
      const amount = Number(item.amount || 0);
      row.qty += qty;
      row.amount += amount;
      row.invoice_count += 1;
      day.total_qty += qty;
      day.total_amount += amount;
    }
  }

  return Array.from(byDate.values())
    .map((day) => ({
      ...day,
      items: Array.from(day.items.values()).sort((a, b) =>
        String(a.name).localeCompare(String(b.name))
      ),
    }))
    .sort((a, b) => String(b.date).localeCompare(String(a.date)));
};

export const filterDateItemSalesSummary = (days, searchText) => {
  const q = String(searchText || "")
    .trim()
    .toLowerCase();
  if (!q) return days;
  return days
    .map((day) => {
      const items = day.items.filter(
        (item) =>
          String(item.name || "")
            .toLowerCase()
            .includes(q) ||
          String(item.unit || "")
            .toLowerCase()
            .includes(q)
      );
      if (
        !items.length &&
        !String(day.date).includes(q) &&
        !formatSaleDateLabel(day.date).toLowerCase().includes(q)
      ) {
        return null;
      }
      const matchedItems = items.length ? items : day.items;
      const useFiltered = items.length > 0;
      const list = useFiltered ? items : matchedItems;
      return {
        ...day,
        items: list,
        total_qty: list.reduce((s, i) => s + Number(i.qty || 0), 0),
        total_amount: list.reduce((s, i) => s + Number(i.amount || 0), 0),
      };
    })
    .filter(Boolean);
};

export const sumDateItemSales = (days = []) =>
  (Array.isArray(days) ? days : []).reduce(
    (acc, day) => {
      acc.qty += Number(day.total_qty || 0);
      acc.amount += Number(day.total_amount || 0);
      acc.invoices += Number(day.invoice_count || 0);
      acc.days += 1;
      return acc;
    },
    { qty: 0, amount: 0, invoices: 0, days: 0 }
  );
